import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useAppSelector } from "../redux-hooks";
import { orderCreate, getOrderItemsById, returnOrder, searchOrder, tissueTracking, orderUpdate, getOrderDocumentByDocumentId, getOrderDocumentsByDocumentIds, orderCancel, previewPackagingPDF, previewSalesInvoicePDF, ancilaryOrderCreate, previewProofOfDeliveryPDF, previewEvaltionFormPDF, previewPrescription, previewCMS1500, register17TrackWebhook, getOrderTrackingLogsData, getRealTimeTrackingInfo, getOrderByPatient, editProductCommissionFromBilling, editCommissionRate, updateOrderItemsPriceApi, getOrderItemsByInvoice, getOrderItemsByClaim, approveProductReturn, rejectProductReturn, pendingProductReturn, getProductReturnAudit, getPlaceOrderCheckApi, fetchPracticesByUserRoleOrder, getOrderById, verifyOrderTrackingLogsData } from "../../../service/OrderService";
import { InterceptorOption, LoadingType } from "../../../types/CommonTypes";
import { updateElementValue } from "../../../utils/updateElementValue";
import { fetchAllProductsByUserRole, fetchProductsByUserRole, fetchProductsIncludingInactiveByUserRoleSkipLoading } from "../../../service/ProductService";
import { fetchPracticesByUserRole } from "../../../service/PracticeService";
import { PracticeInfo, PracticeInfonew } from "../practiceSlice";
import { ProductInfo } from "../product/productListSlice";
import { ClaimDTO, orderItemClaim } from "../claim/ClaimSlice";

export type OrderDocumentInfo = {
  ivrId: any;
  documentId?: number;
  documentName: string;
  content: string;
  documentType?: string;
  type?: string;
  fileType: string;
  auditTimestamp?: string;
};

export type OrderTrackingLogs = {
  trackingLogId: any;
  orderId?: number;
  carrier: string;
  trackingNumber: string;
  status?: string;
  statusUpdateTimestamp?: string;
  detailedTrackingInfo: string;
  auditTimestamp?: string;
};

export type SearchOrder = {
  practiceIds: number[];
  manufacturerIds: number[];
  // CS-905
  patientFirstName: string;
  patientLastName: string;
  //
  productIds: number[];
  providerName: string;
  orderNumber: string;
  invoiceNumber: string;
  serialNumber: string;
  //cs-382
  // dateOfOrder: string;
  fromDate: string | undefined;
  toDate: string | undefined;
  //
  dateOfService?: string;
  status: string[];
  invoiceStatus: string[];
  currentPage: number;
  itemsPerPage: number;
  downloadState: boolean;
  orderItemStatus?: string[];
  fromServiceDate: string | undefined;
  toServiceDate: string | undefined;
  withoutIvr?: string;
  // dateRange?: {
  //   fromDate: string;
  //   toDate: string;
  // };
  dateRange?: string;
  patientId?: number;
  alertOrders?: boolean;
  orderIds?: number[];
  claimStatus?: string[];
  // Optional. Absent values must keep the existing search order.
  sortBy?: "orderNumber" | "practiceName" | "patientName" | "orderDate" | "dateOfService" | "manufacturerName" | "productName" | "providerName" | "orderStatus";
  sortOrder?: "asc" | "desc";
};

export type CreateOrder = {
  practice: string;
  patient: string;
  product: string;
  provider: string;
  dateOrdered: string;
  dateOfService: string;
  status: string;
};

export type TissueTrackingDetail = {
  patient: string;
  product: string;
  order: string;
  po: string;
  dateOfService: string;
  shippingCarrier?: string;
  trackingNumber?: string;
  orderStatus?: string;
  providerName?: string;
  practiceName?: string;
};

export type GraftQty = {
  productItemId: number,
  quantity: number
}

export type PatientDetails = {
  firstName: string,
  middleName?: string,
  lastName: string,
  birthDate: string,
  gender: string
}

export type AddOrderInfo = {
  practiceId: number | string,
  ivrId?: number,
  productId: number | string | undefined,
  practiceProductId: string | number | undefined,
  patientId?: number,
  orderType: string,
  // orderDate: string,
  requestedDeliveryDate: string,
  requestedShippingMethod: string,
  requestedShippingConfigId?: number | null, // CS-1766 - Shipping config ID for product-specific shipping
  dateOfService: string,
  poNumber?: string | undefined,
  orderInfo: {
    practice: string,
    patient?: string,
    provider?: string,
    facilityAddress: string,
    providerId?: string | number
    manufacturerEmailEnable?: boolean
  },
  orderItems: GraftQty[] | undefined,
  orderId?: number,
  patient?: PatientDetails,
  orderDate?: string,
  manufacturerEmailEnable?: boolean,
  upfrontPayment?: boolean
}

export type OrderReturn = {
  orderId: number;
  productId: number;
  returnReason: string;
  returnDate: string;
  otherReturnReason: string;
  signature: string;
  name: string;
  productNotDamaged: boolean,
  productNotTransfer: boolean,
  productStoredInRecommendedEnv: boolean,
  isShippingLabel: boolean,
  orderItems: {
    orderItemId: number;
    serialNumber?: string;
  }[];

  returnTrackingInfo: {
    returnCarrier: string;
    returnTracking: string;
  };
  attachmentBarcodeSticker: {
    documentName: string;
    content: string;
    fileType: string;
  }[];
  attachmentProductBoxPictures: {
    documentName: string;
    content: string;
    fileType: string;
  }[];
  additionalContext: string;

};

export type TissueTracking = {
  orderId: number;
  patientId: number;
  productId: number;
  dateOfService: string;
  orderItems: {
    orderItemId: number;
    serialNumber: string;
  }[];
  attachmentBarcodeSticker: {
    documentName: string;
    content: string;
    fileType: string;
  }[];
};

export type OrderDetails = {
  practiceIds: number[];
  productIds: number[];
  patientFirstName: string;
  patientLastName: string;
  providerName: string;
  orderNumber: string;
  // dateOfOrder: string;
  //cs-387
  fromDate: string | undefined;
  toDate: string | undefined;
  //
  // dateOfService: string;
  status: string[];
  fromServiceDate: string | undefined;
  toServiceDate: string | undefined;
  alertOrderNumbers?: number[] | [];
};

export interface OrderItemDTO {
  orderItemId: number;
  orderId: number;
  productItemId: number;
  size: string;
  productPriceId: number;
  quantity: number;
  tissueTrackingId: number;
  tissueTrackingDocumentId: number;
  productReturnId: number;
  productReturnStatus: string;
  returnReason: string;
  returnDocumentId: number;
  serialNumber: string;
  auditUser: string;
  auditTimestamp: string;
  claimId?: string;
  claimNumber?: string;
  claim: orderItemClaim,
  invoice: orderItemInvoice,
  tissueTrackingInfo: TissueTrackingInfo,
  practiceProductId: string
  invoiceId: number;
  returnTrackingInfo: {
    returnCarrier?: string;
    returnTracking?: string;
    additionalContext?: string;
    productBoxPictures?: number[];
    barcodes?: number[];
    otherReturnReason?: string;
    returnRequestComments?: string;
  };
  units: number;
  extendedPrice: number;
  orderItemPrice: number; //CS-1560
  orderTotalPrice: number; //CS-1560
  actualDateOfService: string
  appliedDate: string;
  returnedDate: string;
  invoicedDate: string;
  manufacturerName: string;
  mfgInvoiceNumber: string;
  hasSKUPrice: boolean;
  productItemDescription?: boolean;
  isPlaceHolderItem?: boolean;
  productId?: number;
  hcpcsCode?: string;
  allowableUnitPrice?: number;
  isCommissionPaid?: boolean;
  isPaymentFound?: boolean;
  inactiveDate?: string | null;
}

export interface PracticeProductAgreementDTO {
  practiceProductId: number;
  practiceName: string;
  productName: string;
  extendedPrice: number;
  discountType: string;
  effectiveDate: string;
  inactiveDate: string | null;
  agreementStatus: string;
  practiceSignedDate: string | null;
  manufacturerSignedDate: string | null;
  productLogo: string | null;
  upfrontPrice?: number | null;
}


export interface OrderInfo {
  orderId: number;
  practiceId: number;
  productId: number;
  ivrId: number;
  ivrNumber?: string;
  canSignOrder?: boolean;
  patientId: number;
  practiceName: string;
  hasMiddleName: boolean;
  patientName: string;
  provider?: string;
  productCategory?: string | null;
  additionalNotes?: string | null;
  manufacturerName: string;
  manufacturerId: number;
  purchaseAndBill?: string | null; // CS-1765
  customConfig?: {
    provider_signature_required: string | null;
    purchase_and_bill: string | null;
    stock_and_bill: string | null;
  }
  productName: string;
  providerName: string;
  orderNumber: string;
  orderDate: string;
  dateOfService?: string;
  orderType: string;
  orderStatus: string;
  mfgConfirmationNumber: string;
  shippedDate: string;
  deliveredDate: string;
  processedDate: string;
  shippingCarrier: string;
  trackingNumber: string;
  poNumber: string;
  orderTotalUnit: number;
  orderTotalPrice: number;
  orderDocumentId: number;
  invoiceId?: number;
  invoiceStatus?: string;
  isOutsideOrder?: boolean;
  outsideOrderShippingPrice?: number | null;
  orderInfo?: {
    practice: string,
    patient?: string,
    provider?: string,
    PatientID?: string,
    facilityAddress: string,
    processedDate: string,
    requestedShippingConfigId?: number | null, // CS-1766 - Shipping config ID
    shippingMethod?: string | null, // CS-1766
    shippingMethodId?: number | null, // CS-1766
    shipping_charges?: string | null, // CS-1845 - Stored shipping cost (updated on fulfillment by CS-1766)
    shippingPrice?: string | null,
    isOutsideOrder?: string | boolean | null,
    practiceEntryType?: string | null,
    providerEntryType?: string | null,
    patientEntryType?: string | null,
    productEntryType?: string | null,
    shippingAddressEntryType?: string | null,
    shippingMethodEntryType?: string | null,
    practiceId?: string | number | null,
    providerId?: string | number | null,
    shippingAddressId?: string | number | null,
    practiceProductId?: string | number | null,
    productName?: string | null,
    patientName?: string | null,
    providerName?: string | null,
    practiceName?: string | null,
    shippingAddress?: string | null,
    orderItems?: string | null,
    notes?: string | null,
    orderPrice?: string | number | null,
    total?: string | number | null,
    dateOfService?: string | null,
    discount?: string | null, // CS-1845
    discountAmount?: string | null, // CS-1845
  };
  orderItems: OrderItemDTO[];
  manufacturerOrderId: string;
  manufacturerPatientId: string;
  requestedShippingMethod: string;
  actualShippingMethod?: string;//CS-1766
  requestedDeliveryDate?: string;
  claimId?: string;
  claimNumber?: string;
  cmsDocumentId?: number;
  //CS-1564 : if order is inactive
  manufacturerInactiveDate?: string | null;
  productInactiveDate?: string | null;
  isUpfrontOrder?: boolean; // CS-1845
};

export const hasOutsideOrderItemPayment = (order?: { orderItems?: OrderItemDTO[] | null }) =>
  (order?.orderItems ?? []).some((item) => item.isPaymentFound === true);

interface PaginationOrderList {
  content: OrderInfo[];
  totalElements: number;
  totalPages: number;
  size: number;
}

export type OrderItemInfo = {
  orderItemId: number,
  serialNumber: string
}

export interface OrderFulfillmentInfo {
  orderId: number;
  practiceId: number;
  ivrId: number;
  patientId: number;
  practiceName: string;
  manufacturerName: string;
  patientName: string;
  productName: string;
  providerName: string;
  orderNumber: string;
  orderDate: string;
  orderType: string;
  orderStatus: string;
  mfgConfirmationNumber: string;
  shippedDate: string;
  deliveredDate: string;
  processedDate: string;
  shippingCarrier: string;
  trackingNumber: string;
  poNumber: string;
  dateOfService: string;
  orderStatusToUpdate: string;
  updateStatusDate: string;
  orderItems: OrderItemDTO[];
  manufacturerOrderId: string;
  manufacturerPatientId: string;
  shippingMethod?: string; // CS-1766 - Product-specific shipping method
  shippingMethodId?: number | null; // CS-1766 - Shipping config ID
  shippingPrice?: string | number | null;
  previousShippingCharge?: number | null;
  invoiceUpdateStatus?: string | null;
  invoiceId?: number | null;
  invoiceStatus?: string | null;
  updateInvoiceShipping?: boolean | null;
  orderInfo?: OrderInfo["orderInfo"];
}

export interface orderItemInvoice {
  invoiceId: number,
  manufacturerId: number,
  agencyId: number,
  mfgInvoiceNumber: string,
  mfgInvoiceStatus?: string,
  cogsAmount: number,
  practicePaidDate: string,
  v3CommReceivedDate: string,
  v3ProductCommissionId: number,
  v3CommStatus: string,
  agencyCommStatus: string,
  agencyCommPaidDate: string,
  agencyProductCommissionId: number
};

export type AttachmentBarcodeStickerInfo = {
  documentName: string,
  content: string,
  fileType: string
}

export type AttachmentImageOfWoundsInfo = {
  documentName: string,
  content: string,
  fileType: string
}

export type AttachmentMedicalNotesInfo = {
  documentName: string,
  content: string,
  fileType: string
}

export type TrackingRegistrationRequest = {
  number: string;
  lang?: string;
  email?: string;
  param?: string;
  order_no: string;
  order_time: string;
  carrier: number;
  final_carrier?: number;
  auto_detection?: boolean;
  tag?: string;
  remark?: string;
  previous_number?: string;
  previous_carrier?: number;
}

export type FetchOrderDocumentsByIdsArg =
  | number
  | number[]
  | { documentIds: number | number[]; skipLoader?: boolean };

export const initialValuesOfOrderFulfillment = {
  orderId: 0,
  practiceId: 0,
  ivrId: 0,
  patientId: 0,
  practiceName: "",
  patientName: "",
  productName: "",
  manufacturerName: "",
  providerName: "",
  orderNumber: "",
  orderDate: "",
  orderType: "",
  orderStatus: "",
  mfgConfirmationNumber: "",
  shippedDate: "",
  deliveredDate: "",
  processedDate: "",
  shippingCarrier: "",
  trackingNumber: "",
  poNumber: "",
  dateOfService: "",
  orderStatusToUpdate: "",
  updateStatusDate: "",
  orderItems: [{}],
  manufacturerOrderId: "",
  manufacturerPatientId: "",
  shippingMethod: "", // CS-1766
  shippingMethodId: null, // CS-1766
  shippingPrice: null,
  orderInfo: undefined,
} as OrderFulfillmentInfo;

export interface TissueTrackingInfo {
  barcodes: number[];
  imagesOfWounds: number[];
  medicalNotes: number[];
  orderId: number,
  productId: number,
  patientId?: number,
  dateOfService: string,
  orderItems: OrderItemInfo[],
  attachmentBarcodeSticker: AttachmentBarcodeStickerInfo[],
  attachmentImageOfWounds: AttachmentImageOfWoundsInfo[],
  attachmentMedicalNotes: AttachmentMedicalNotesInfo[],
  serialNumbers?: string[],
  // CS-746
  woundLocations?: string[],
  comment?: string,
  isBarcodeStickerSkipped: boolean,
  manufacturerOrderId: string;

}

export type ClaimNoteDetail = {
  noteId?: number;
  xrefTable: string;
  xrefId: number;
  noteType: string;
  content?: string;
  attachementId?: number;
  documentName?: string;
  fileType?: string;
  auditUserName?: string,
  auditTimestamp?: string,
  comments?: string,
};

//CS-1164 START
export type ProductReturnApprovalRequest = {
  productReturnIds: number[];
  orderNumber: string;
  orderId: number;
  productId: number;
  responseStatus: string;
  message?: string;
  // Optional reference document uploaded at reject time.
  productReturnRejectDocument?: ProductReturnPendingDocument;
};
//CS-1164 END

// Mark Product Return as Pending
export type ProductReturnPendingDocument = {
  documentName: string;
  fileType: string;
  content: string;
};

export type ProductReturnPendingRequest = {
  productReturnIds: number[];
  orderId: number;
  productReturnPendingDocument?: ProductReturnPendingDocument;
  message?: string;
};
//CS-1560
interface orderItemsUpdatedPriceRequest {
  productItemId: number;
  currentPrice: number;
  newPrice: number;
}
export type OrderItemsPriceUpdateRequest = {
  orderId: number;
  orderItems: orderItemsUpdatedPriceRequest[]
}

type PlacedOrderCheckByProviderAndPractice = {
  canDirectPlaceOrder: boolean;
}
type OrderState = {
  orderItemsListInfo?: OrderItemDTO[] | null;
  addTissueTrackingStatus: LoadingType;
  status: LoadingType;
  editProductCommissionStatus: LoadingType;
  cancelStatus: LoadingType;
  orderListInfo?: PaginationOrderList | null;
  orderDocumentInfo?: OrderDocumentInfo | null;
  orderTrackingLogs?: OrderTrackingLogs | null;
  fetchProductsByRoleStatusOrderSlice: LoadingType,
  //CS-1564 
  fetchAllProductsByRoleStatusOrderSlice: LoadingType,
  productsByUserRoleOrderSlice: ProductInfo[],
  //CS-1564
  allProductsByUserRoleOrderSlice: ProductInfo[],
  getPracticesByRoleStatusOrderSlice: LoadingType;
  practiceByUserRoleOrderSlice?: PracticeInfo[] | null;
  practiceByUserRoleOrderSlicenew?: PracticeInfonew[] | null;
  ordersByPatient?: OrderInfo[] | null;
  claimInfo?: ClaimDTO | null;
  claimNoteInfo?: ClaimNoteDetail | [];
  //CS-1534 - Provider phase 2 change 
  placeOrderPreCheckWithProviderAndPracticeId?: PlacedOrderCheckByProviderAndPractice;
  OrderFetchStatus: LoadingType;
  OrderObject?: OrderInfo;
}

const initialState: OrderState = {
  orderItemsListInfo: undefined,
  addTissueTrackingStatus: 'idle',
  status: "idle",
  editProductCommissionStatus: "idle",
  cancelStatus: 'idle',
  orderListInfo: undefined,
  orderDocumentInfo: undefined,
  orderTrackingLogs: undefined,
  fetchProductsByRoleStatusOrderSlice: "idle",
  //CS-1564
  fetchAllProductsByRoleStatusOrderSlice: "idle",
  productsByUserRoleOrderSlice: [],
  //CS-1564
  allProductsByUserRoleOrderSlice: [],
  getPracticesByRoleStatusOrderSlice: "idle",
  practiceByUserRoleOrderSlice: [],
  practiceByUserRoleOrderSlicenew: [],
  claimInfo: undefined,
  claimNoteInfo: undefined,
  OrderFetchStatus: "idle"
};

export const addOrder = createAsyncThunk("addOrder", async (data: AddOrderInfo | null) => {
  const response = await orderCreate(data);
  const v3Response = response.data;
  return v3Response.data;
});
//cs-793 ancillary order
export const addAncillaryOrder = createAsyncThunk("addAncillaryOrder", async (data: any | null) => {
  const response = await ancilaryOrderCreate(data);
  const v3Response = response.data;
  return v3Response.data;
});
//

export const updateOrder = createAsyncThunk<any, { orderUpdataData: OrderFulfillmentInfo | null, interceptorOption: InterceptorOption }>("updateOrder", async (data, { rejectWithValue }) => {
  const { orderUpdataData, interceptorOption } = data
  try {
    const response = await orderUpdate(orderUpdataData, interceptorOption);
    const resData = response.data;
    return resData;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});

export const getOrderItemsByOrderId = createAsyncThunk("getOrderItemsById", async (id: number) => {
  const response = await getOrderItemsById(id);
  const v3Response = response.data;
  return v3Response;
});

export const addProductReturn = createAsyncThunk("OrderReturn", async (data: OrderReturn | null) => {
  const response = await returnOrder(data)
  const v3Response = response.data;
  return v3Response;
});

export const fetchOrdersList = createAsyncThunk<any, { data: OrderDetails, skipLoader?:boolean }>("fetchOrdersList", async ({ data, skipLoader=false }) => {
  const response = await searchOrder(data, { 'x-skip-loader': skipLoader.toString() });
  const v3Response = response.data;
  return v3Response;
});

export const addTissueTracking = createAsyncThunk<any, { tissueTrackingData: TissueTrackingInfo, interceptorOption: InterceptorOption }>("addTissueTracking", async (data, { rejectWithValue }) => {
  const { tissueTrackingData, interceptorOption } = data
  try {
    const response = await tissueTracking(tissueTrackingData, interceptorOption);
    const v3Response = response.data;
    return v3Response;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});

export const fetchOrderDocumentById = createAsyncThunk('fetchOrderDocumentById', async (documentId: number) => {
  const response = await getOrderDocumentByDocumentId(documentId);
  const v3Response = response.data;
  return v3Response.data;
});
// CS- 922 
export const previewPackagePDF = createAsyncThunk('previewPackagePDF', async (orderId: number) => {
  const response = await previewPackagingPDF(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//
// CS - 731
export const previewSalesPDF = createAsyncThunk('previewSalesPDF', async (orderId: number) => {
  const response = await previewSalesInvoicePDF(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//
// CS - 936
export const previewProofOfDelivery = createAsyncThunk('previewProofOfDelivery', async (orderId: number) => {
  const response = await previewProofOfDeliveryPDF(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//
// CS - 927
export const previewEvaltionForm = createAsyncThunk('previewEvaltionForm', async (orderId: number) => {
  const response = await previewEvaltionFormPDF(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//
// CS - 926
export const previewPrescriptionPDF = createAsyncThunk('previewPrescriptionPDF', async (orderId: number) => {
  const response = await previewPrescription(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//
// CS - 934
export const previewCMSPDF = createAsyncThunk('previewCMSPDF', async (orderId: number) => {
  const response = await previewCMS1500(orderId);
  const v3Response = response.data;
  return v3Response.data;
});
//

export const fetchOrderDocumentsByIds = createAsyncThunk(
  "fetchOrderDocumentsByIds",
  async (arg: FetchOrderDocumentsByIdsArg) => {
    let documentIds: number[];
    let skipLoader = false;
    if (typeof arg === "object" && arg !== null && !Array.isArray(arg) && "documentIds" in arg) {
      const raw = arg.documentIds;
      documentIds = Array.isArray(raw) ? raw : [raw];
      skipLoader = Boolean(arg.skipLoader);
    } else {
      documentIds = Array.isArray(arg) ? arg : [arg];
    }
    const response = await getOrderDocumentsByDocumentIds(
      documentIds,
      skipLoader ? { headers: { "x-skip-loader": "true" } } : undefined
    );
    const v3Response = response.data;
    return v3Response.data;
  }
);

export const fetchProductReturnAuditById = createAsyncThunk("fetchProductReturnAuditById", async (productReturnId: number) => {
  const response = await getProductReturnAudit(productReturnId);
  const v3Response = response.data;
  return v3Response.data;
});

export const editProductCommissionRateFromBilling = createAsyncThunk("editProductCommissionRateFromBilling", async (data: any) => {
  const response = await editProductCommissionFromBilling(data);

  return response;
})

export const editCommissionRateAction = createAsyncThunk(
  "editCommissionRateAction",
  async (data: any, { rejectWithValue }) => {
    try {
      const skipLoader = !!data?.skipLoader;
      const payload = skipLoader
        ? (({ skipLoader: _skip, ...rest }) => rest)(data)
        : data;
      const response = skipLoader
        ? await editCommissionRate(payload, true)
        : await editCommissionRate(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? { message: error?.message });
    }
  }
)

export const fetchProductListByUserRoleOrderSlice = createAsyncThunk("fetchProductListByUserRoleOrderSlice", async (includeProductPrice: boolean) => {
  const response = await fetchProductsByUserRole(includeProductPrice);
  const v3Response = response.data
  return v3Response.data;
});

export const getPracticeListByUserRoleOrderSlice = createAsyncThunk('getPracticeListByUserRoleOrderSlice', async (isActivePracticeOnly?: boolean) => {
  const response = await fetchPracticesByUserRole(isActivePracticeOnly);
  const v3Response = response.data;
  return v3Response.data;
});


export const fetchProductListIncludingInactiveByUserRoleOrderSlice = createAsyncThunk<any, { includeProductPrice: boolean; executedOnly: boolean, skipLoader?: boolean }>("fetchProductListIncludingInactiveByUserRoleOrderSlice", async ({ includeProductPrice, executedOnly, skipLoader = false }) => {
  const response = await fetchProductsIncludingInactiveByUserRoleSkipLoading(includeProductPrice, executedOnly, { 'x-skip-loader': skipLoader.toString() });
  const v3Response = response.data
  return v3Response.data;
});
//CS-1564 - New api for product search to get all products by user role
export const fetchAllProductListByUserRoleOrderSlice = createAsyncThunk<any, { includeProductPrice: boolean; executedOnly: boolean, skipLoader?: boolean }>("fetchAllProductListByUserRoleOrderSlice", async ({ includeProductPrice, executedOnly, skipLoader = false }) => {
  const response = await fetchAllProductsByUserRole(includeProductPrice, executedOnly, { 'x-skip-loader': skipLoader.toString() });
  const v3Response = response.data
  return v3Response.data;
});

export const getPracticeListByUserRoleOrderSlicenew = createAsyncThunk<any, { isActivePracticeOnly?: boolean, skipLoader?: boolean }>("getPracticeListByUserRoleOrderSlicenew", async ({ isActivePracticeOnly, skipLoader = false }) => {
  const response = await fetchPracticesByUserRoleOrder(!!isActivePracticeOnly, {
    'x-skip-loader': skipLoader.toString(),
  });
  const v3Response = response.data;
  return v3Response.data;
});

export const cancelOrder = createAsyncThunk("cancelOrder", async (orderId: number) => {
  const response = await orderCancel(orderId);
  const v3Response = response.data;
  return v3Response.data;
});

export const getOrderTrackingLogs = createAsyncThunk('getOrderTrackingLogs', async ({ orderId, trackingNumber }: { orderId: number, trackingNumber: string }) => {
  const response = await getOrderTrackingLogsData(orderId, trackingNumber);
  const v3Response = response.data;
  return v3Response.data;
});

export const registerWebhookTracking = createAsyncThunk("registerWebhookTracking", async (data: TrackingRegistrationRequest[]) => {
  const response = await register17TrackWebhook(data);
  const v3Response = response.data;
  return v3Response.data;
});

export const getRealTimeTrackInfo = createAsyncThunk("getRealTimeTrackInfo", async ({ trackingNumber, carrier, orderId }: { trackingNumber: string, carrier: number, orderId: number }) => {
  const response = await getRealTimeTrackingInfo(trackingNumber, carrier, orderId);
  const v3Response = response.data;
  return v3Response.data;
});

export const getOrdersByPatientId = createAsyncThunk("getOrderByPatientId", async (patientId: number) => {
  const response = await getOrderByPatient(patientId);
  const v3Response = response.data;
  return v3Response;
});

export const getOrderItemsByInvoiceId = createAsyncThunk("getOrderItemsByInvoiceId", async (id: number) => {
  const response = await getOrderItemsByInvoice(id);
  const v3Response = response.data;
  return v3Response;
});

export const getOrderItemsByClaimId = createAsyncThunk("getOrderItemsByClaimId", async (id: number) => {
  const response = await getOrderItemsByClaim(id);
  const v3Response = response.data;
  return v3Response;
});

//CS-1164 START
export const approveProductReturnRequest = createAsyncThunk("approveProductReturnRequest", async (data: ProductReturnApprovalRequest) => {
  const response = await approveProductReturn(data);
  const v3Response = response.data;
  return v3Response;
});
//CS-1164 END

// CS-1164 - Reject Return Request
export const rejectProductReturnRequest = createAsyncThunk("rejectProductReturnRequest", async (data: ProductReturnApprovalRequest) => {
  const response = await rejectProductReturn(data);
  const v3Response = response.data;
  return v3Response;
});

// Mark Product Return as Pending
export const pendingProductReturnRequest = createAsyncThunk("pendingProductReturnRequest", async (data: ProductReturnPendingRequest) => {
  const response = await pendingProductReturn(data);
  const v3Response = response.data;
  return v3Response;
});

//CS-1534 - Provider phase 2 new api slice 
export const placeOrderCheckBasedOnProviderAndPracticeId = createAsyncThunk("placeOrderCheckBasedOnProviderAndPracticeId", async ({ practiceId, providerId }: { practiceId: number, providerId: number }) => {
  const response = await getPlaceOrderCheckApi(practiceId, providerId);
  const v3Response = response.data;
  return v3Response.data;
});

export const fetchOrderById = createAsyncThunk("fetchOrderById", async ( {orderId, isSaveAuditLog} : {orderId: number, isSaveAuditLog: boolean} ) => {
  const response = await getOrderById(orderId, isSaveAuditLog);
  const v3Response = response.data;
  return v3Response;
});

//CS-1560 - Update orderitem price api slice
export const updateOrderItemsPrice = createAsyncThunk("updateOrderItemsPrice", async (data: OrderItemsPriceUpdateRequest) => {
  const response = await updateOrderItemsPriceApi(data);
  const v3Response = response.data;
  return v3Response;
});

export const verifyOrderTrackingLogs = createAsyncThunk('verifyOrderTrackingLogs', async ({ trackingNumber }: { trackingNumber: string }) => {
  const response = await verifyOrderTrackingLogsData(trackingNumber);
  const v3Response = response.data;
  return v3Response.data;
});

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    updateElementInOrderSlice: updateElementValue,
    resetOrderListInfo: (state: OrderState) => {
      state.orderListInfo = undefined;
      state.fetchProductsByRoleStatusOrderSlice = "idle";
      //CS-1564
      state.fetchAllProductsByRoleStatusOrderSlice = "idle";
      state.productsByUserRoleOrderSlice = [];
      //CS-1564
      state.allProductsByUserRoleOrderSlice = [];
      state.getPracticesByRoleStatusOrderSlice = "idle";
      state.practiceByUserRoleOrderSlice = [];
      state.practiceByUserRoleOrderSlicenew = [];
    },
    resetCancelStatus: (state: OrderState) => {
      state.cancelStatus = "idle";
    },
    resetOrdersByPatient: (state: OrderState) => {
      state.ordersByPatient = [];
    },
    //CS-1322 - Reseting the orderitems for Create/Record Invoice
    resetOrderItemListInfo: (state: OrderState) => {
      state.orderItemsListInfo = [];
    },
    resetOrderDocumentInfo: (state: OrderState) => {
      state.orderDocumentInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addOrder.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        state.status = "success"
      })
      .addCase(addOrder.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getOrderItemsByOrderId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getOrderItemsByOrderId.fulfilled, (state, action) => {
        state.status = "success"
        // state.orderItemsListInfo = action.payload.data
        state.orderItemsListInfo = action.payload?.data // CS-1984
      })
      .addCase(getOrderItemsByOrderId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(addProductReturn.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addProductReturn.fulfilled, (state, action) => {
        state.status = "success"
      })
      .addCase(addProductReturn.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(fetchOrdersList.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(fetchOrdersList.fulfilled, (state, action) => {
        state.status = "success";
        // state.orderListInfo = action.payload.data;
        state.orderListInfo = action.payload?.data; // CS-1984
      })
      .addCase(fetchOrdersList.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(addTissueTracking.pending, (state, action) => {
        state.addTissueTrackingStatus = "loading"
      })
      .addCase(addTissueTracking.fulfilled, (state, action) => {
        state.addTissueTrackingStatus = "success"
      })
      .addCase(addTissueTracking.rejected, (state, action) => {
        state.addTissueTrackingStatus = "error"
      })
      .addCase(editProductCommissionRateFromBilling.pending, (state, action) => {
        state.editProductCommissionStatus = "loading"
      })
      .addCase(editProductCommissionRateFromBilling.fulfilled, (state, action) => {
        state.editProductCommissionStatus = "success"
      })
      .addCase(editProductCommissionRateFromBilling.rejected, (state, action) => {
        state.editProductCommissionStatus = "error"
      })
      .addCase(editCommissionRateAction.pending,(state,action) => {
        state.editProductCommissionStatus = "loading"
      })
      .addCase(editCommissionRateAction.fulfilled, (state, action) => {
        state.editProductCommissionStatus = "success"
      })
      .addCase(editCommissionRateAction.rejected, (state, action) => {
        state.editProductCommissionStatus = "error"
      })
      .addCase(fetchOrderDocumentById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchOrderDocumentById.fulfilled, (state, action) => {
        state.status = "success";
        state.orderDocumentInfo = action.payload;
      }
      )
      .addCase(fetchOrderDocumentById.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(fetchOrderDocumentsByIds.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchOrderDocumentsByIds.fulfilled, (state, action) => {
        state.status = "success";
        state.orderDocumentInfo = action.payload;
      }
      )
      .addCase(fetchOrderDocumentsByIds.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(fetchProductReturnAuditById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProductReturnAuditById.fulfilled, (state, action) => {
        state.status = "success";
      }
      )
      .addCase(fetchProductReturnAuditById.rejected, (state, action) => {
        state.status = "error";
      })
      .addCase(fetchProductListByUserRoleOrderSlice.pending, (state) => {
        state.fetchProductsByRoleStatusOrderSlice = "loading";
      })
      .addCase(fetchProductListByUserRoleOrderSlice.fulfilled, (state, action) => {
        state.fetchProductsByRoleStatusOrderSlice = "success";
        state.productsByUserRoleOrderSlice = action.payload;
      })
      .addCase(fetchProductListByUserRoleOrderSlice.rejected, (state, action) => {
        state.fetchProductsByRoleStatusOrderSlice = 'error';
      })
      .addCase(fetchProductListIncludingInactiveByUserRoleOrderSlice.pending, (state) => {
        state.fetchProductsByRoleStatusOrderSlice = "loading";
      })
      .addCase(fetchProductListIncludingInactiveByUserRoleOrderSlice.fulfilled, (state, action) => {
        state.fetchProductsByRoleStatusOrderSlice = "success";
        state.productsByUserRoleOrderSlice = action.payload;
      })
      .addCase(fetchProductListIncludingInactiveByUserRoleOrderSlice.rejected, (state, action) => {
        state.fetchProductsByRoleStatusOrderSlice = 'error';
      })
      // CS-1564 
      .addCase(fetchAllProductListByUserRoleOrderSlice.pending, (state) => {
        state.fetchAllProductsByRoleStatusOrderSlice = "loading";
      })
      .addCase(fetchAllProductListByUserRoleOrderSlice.fulfilled, (state, action) => {
        state.fetchAllProductsByRoleStatusOrderSlice = "success";
        state.allProductsByUserRoleOrderSlice = action.payload;
      })
      .addCase(fetchAllProductListByUserRoleOrderSlice.rejected, (state, action) => {
        state.fetchAllProductsByRoleStatusOrderSlice = 'error';
      })
      .addCase(getPracticeListByUserRoleOrderSlice.pending, (state, action) => {
        state.getPracticesByRoleStatusOrderSlice = 'loading';
      })
      .addCase(getPracticeListByUserRoleOrderSlice.fulfilled, (state, action) => {
        state.getPracticesByRoleStatusOrderSlice = 'success';
        state.practiceByUserRoleOrderSlice = action.payload;
      })
      .addCase(getPracticeListByUserRoleOrderSlicenew.fulfilled, (state, action) => {
        state.getPracticesByRoleStatusOrderSlice = 'success';
        state.practiceByUserRoleOrderSlicenew = action.payload;
      })
      .addCase(getPracticeListByUserRoleOrderSlice.rejected, (state, action) => {
        state.getPracticesByRoleStatusOrderSlice = 'error';
      })
      .addCase(cancelOrder.pending, (state, action) => {
        state.cancelStatus = "loading"
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.cancelStatus = "success"
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.cancelStatus = "error"
      })
      .addCase(previewPackagePDF.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewPackagePDF.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewPackagePDF.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(previewSalesPDF.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewSalesPDF.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewSalesPDF.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(previewProofOfDelivery.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewProofOfDelivery.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewProofOfDelivery.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(previewEvaltionForm.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewEvaltionForm.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewEvaltionForm.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(previewPrescriptionPDF.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewPrescriptionPDF.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewPrescriptionPDF.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(previewCMSPDF.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(previewCMSPDF.fulfilled, (state, action) => {
        state.status = "success"
        state.orderDocumentInfo = action.payload;
      })
      .addCase(previewCMSPDF.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getOrderTrackingLogs.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getOrderTrackingLogs.fulfilled, (state, action) => {
        state.status = "success"
        state.orderTrackingLogs = action.payload;
      })
      .addCase(getOrderTrackingLogs.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getOrdersByPatientId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getOrdersByPatientId.fulfilled, (state, action) => {
        state.status = "success"
        // state.ordersByPatient = action.payload.data;
        state.ordersByPatient = action.payload?.data; // CS-1984
      })
      .addCase(getOrdersByPatientId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getOrderItemsByInvoiceId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getOrderItemsByInvoiceId.fulfilled, (state, action) => {
        state.status = "success"
        state.orderItemsListInfo = action.payload.data
      })
      .addCase(getOrderItemsByInvoiceId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getOrderItemsByClaimId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getOrderItemsByClaimId.fulfilled, (state, action) => {
        state.status = "success"
        state.orderItemsListInfo = action.payload.data
      })
      .addCase(getOrderItemsByClaimId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(approveProductReturnRequest.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(approveProductReturnRequest.fulfilled, (state, action) => {
        state.status = "success"
      })
      .addCase(approveProductReturnRequest.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(rejectProductReturnRequest.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(rejectProductReturnRequest.fulfilled, (state, action) => {
        state.status = "success"
      })
      .addCase(rejectProductReturnRequest.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(pendingProductReturnRequest.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(pendingProductReturnRequest.fulfilled, (state, action) => {
        state.status = "success"
      })
      .addCase(pendingProductReturnRequest.rejected, (state, action) => {
        state.status = "error"
      })
      //CS-1534 - Provider phase 2 change 
      .addCase(placeOrderCheckBasedOnProviderAndPracticeId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(placeOrderCheckBasedOnProviderAndPracticeId.fulfilled, (state, action) => {
        state.status = "success";
        state.placeOrderPreCheckWithProviderAndPracticeId = action.payload;
      })
      .addCase(placeOrderCheckBasedOnProviderAndPracticeId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(fetchOrderById.pending, (state) => {
        state.OrderFetchStatus = "loading";
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.OrderFetchStatus = "success";
        // state.OrderObject = action.payload.data;
        state.OrderObject = action.payload?.data; // CS-1984
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.OrderFetchStatus = 'error';
      })
  },
});

export const { updateElementInOrderSlice, resetOrderListInfo, resetCancelStatus, resetOrdersByPatient, resetOrderItemListInfo, resetOrderDocumentInfo } = orderSlice.actions;
export const useOrderSlice = () => useAppSelector((state) => state.orderSlice);
export default orderSlice.reducer;

